import { Button, Card, Checkbox, Col, Divider, Flex, Form, Input, Popconfirm, Radio, Row } from "antd";
import { Field } from "./Field";
import { generateRandomString } from "../../../shared/utils";
import { InputField } from "./InputField";
import { RadioGroupField } from "./RadioGroupField";
import { SelectField } from "./SelectField";
import { AutocompleteField } from "./AutocompleteField";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";

export enum Direction {
    HORIZONTAL = "horizontal",
    VERTICAL = "vertical"
}

export const FormContainerTitle = ({ title }: { title: string }) => (
    <>
        <Flex justify="left" align="center" className="mb-4">
            <div className={`text-center text-base font-semibold`}>
                {title}
            </div>
        </Flex>
    </>
)



export class ContainerField implements Field {

    id = generateRandomString(10);
    name = 'Container';
    label = 'Container'
    description = 'A container for grouping fields';
    direction = Direction.HORIZONTAL;
    children: Field[] = [];
    formTag = 'ContainerField';
    childrenPerRow = 3;
    hidden = false;
    transparent = false;
    parent = null;

    /*render = () => (
        <div>
            <h4>{this.label}</h4>
            <Flex vertical={this.direction == Direction.VERTICAL} gap={8}>
                {this.children.map(child => (
                    <div key={child.id}
                        style={{ width: `calc(100% / ${this.children.length})` }}
                    >
                        {child.render()}
                    </div>
                ))}
            </Flex>
        </div>
    )*/
    render = () => (
        <div className={this.hidden ? 'hidden' : undefined}>
            <div className="mb-4">
                <Card
                    className={this.transparent ? undefined : "shadow-lg"}
                    style={
                        this.transparent
                            ? {
                                background: "transparent",
                                border: "none",
                                boxShadow: "none",
                            }
                            : undefined
                    }
                >
                    <FormContainerTitle title={this.label} />
                    <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
                        {this.children.map(child => (
                            <Col key={child.id}
                                xs={child.colSize?.xs ?? 24}
                                sm={child.colSize?.sm ?? 12}
                                md={child.colSize?.md ?? 8}
                                lg={child.colSize?.lg ?? 8}
                                xl={child.colSize?.xl ?? 8}
                            >
                                {child.render()}
                            </Col>
                        ))}
                    </Row>
                </Card>
            </div>
        </div>
    )

    renderEditor = (field, selected, setSelected, deleteComponent) => (
        <ContainerFieldEditor field={field} selected={selected} setSelected={setSelected} deleteComponent={deleteComponent} />
    )

    renderPropertiesEditor = (field: Field, updateField: any) => (
        <ContainerFieldPropsEditor inputField={field} setInputField={updateField} />
    )

    static fromJSON = (json: any, parent = null) => {
        const container = new ContainerField();
        container.id = json.id;
        container.label = json.label;
        container.name = json.name;
        container.description = json.description;
        container.direction = json.direction;
        container.hidden = json.hidden ?? false;
        container.transparent = json.transparent ?? false;
        container.children = json.children.map(child => {
            if (child.formTag === 'InputField') {
                return InputField.fromJSON(child, container);
            }
            if (child.formTag === 'RadioGroupField') {
                return RadioGroupField.fromJSON(child, container);
            }
            if (child.formTag === 'SelectField') {
                return SelectField.fromJSON(child, container);
            }
            if (child.formTag === 'AutocompleteField') {
                return AutocompleteField.fromJSON(child, container);
            }
        }).filter(Boolean);
        //console.log('Container from JSON', container);
        container.parent = parent;
        return container;
    }

    /*toJSON = () => {
        return {
            id: this.id,
            name: this.name,
            description: this.description,
            label: this.label,
            direction: this.direction,
            children: this.children.map(child => child.toJSON()),
            formTag: this.formTag
        }
    }*/
}

export const ContainerFieldEditor = ({ field, selected, setSelected, deleteComponent }) => {

    const onClick = (event, child) => {
        event.stopPropagation();
        console.log('clicked child', child);
        setSelected(child);
    }

    const t = useTranslate();
    return (
        <div className={field.transparent ? "p-4 border border-dashed border-slate-300" : "p-4 bg-slate-200"}>

            <Flex justify="space-between">
                <h4>
                    {field.label}
                    {field.hidden && (
                        <span className="ml-2 text-gray-400 text-xs">({t('forms.fieldHidden')})</span>
                    )}
                </h4>
                <Popconfirm
                    title="Delete the item"
                    description="Are you sure to delete this item?"
                    onConfirm={() => deleteComponent(field)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button size='small' danger icon={<DeleteOutlined />} className="" />
                </Popconfirm>
            </Flex>
            <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
                {field.children.map((child) => (
                    <Col
                        key={child.id}
                        xs={child.colSize?.xs ?? 24}
                        sm={child.colSize?.sm ?? 12}
                        md={child.colSize?.md ?? 8}
                        lg={child.colSize?.lg ?? 8}
                        xl={child.colSize?.xl ?? 8}
                    >
                        <div
                            onClick={(event) => onClick(event, child)}
                            className={
                                selected === child
                                    ? "border-blue-300 border-2 border-dashed p-2 mb-2"
                                    : "hover:border-blue-300 hover:border-1 hover:border-solid p-2 mb-2"
                            }
                        >
                            {child.renderEditor(child, selected, setSelected, deleteComponent)}
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );
}

export const ContainerFieldPropsEditor = ({ inputField, setInputField }: { inputField: Field, setInputField: any }) => {
    const t = useTranslate();

    const handleInputChange = (prop) => (e) => {
        const newVal = { ...inputField, [prop]: e.target.value };
        setInputField(newVal);
    };

    const handleSelectChange = (prop) => (value) => {
        const newVal = { ...inputField, [prop]: value };
        setInputField(newVal);
    };

    const handleCheckboxChange = (prop) => (e) => {
        inputField[prop] = e.target.checked;
        setInputField(inputField);
    };

    const handleRadioChange = (prop) => (e) => {
        const newVal = { ...inputField, [prop]: e.target.value };
        setInputField(newVal);
    }

    return (
        <Form layout="vertical">
            <Form.Item label="Description">
                <Input value={inputField.description} onChange={handleInputChange('description')} />
            </Form.Item>
            <Form.Item label="Label">
                <Input value={inputField.label} onChange={handleInputChange('label')} />
            </Form.Item>
            <Form.Item label="Direction">
                <Radio.Group value={inputField.direction} onChange={handleRadioChange('direction')}>
                    <Radio value={Direction.HORIZONTAL}>Horizontal</Radio>
                    <Radio value={Direction.VERTICAL}>Vertical</Radio>
                </Radio.Group>
            </Form.Item>
            <Form.Item label="Items per row">
                <Input value={inputField.childrenPerRow} onChange={handleInputChange('childrenPerRow')} />
            </Form.Item>
            <Form.Item label={t('forms.fieldHidden')}>
                <Checkbox checked={inputField.hidden ?? false} onChange={handleCheckboxChange('hidden')} />
            </Form.Item>
            <Form.Item label={t('forms.fieldTransparent')}>
                <Checkbox checked={(inputField as any).transparent ?? false} onChange={handleCheckboxChange('transparent')} />
            </Form.Item>
        </Form>
    );
};
